/**
 * E-Prescription API
 *
 * RTK Query endpoints for e-prescription (DoseSpot) integration.
 * Enhances generated endpoints with proper cache invalidation.
 */

import { enhancedApi } from '@store/generated/eprescriptions';

/**
 * Feature-specific tag types for cache invalidation
 */
const TAG_TYPES = ['EprescriptionStatus', 'DeaNumbers'] as const;

/**
 * Enhanced e-prescription API with cache management
 */
export const eprescriptionsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: TAG_TYPES,
  endpoints: {
    getV2ProviderEprescriptionsStatus: {
      providesTags: ['EprescriptionStatus'],
    },
    getV2ProviderEprescriptionsRegistrationStatus: {
      providesTags: ['EprescriptionStatus'],
    },
    postV2ProviderEprescriptionsSetupClinic: {
      invalidatesTags: ['EprescriptionStatus'],
    },
    postV2ProviderEprescriptionsSetupClinician: {
      invalidatesTags: ['EprescriptionStatus'],
    },
    postV2ProviderPatientsByPatientIdEprescriptionsConnect: {
      invalidatesTags: ['EprescriptionStatus'],
    },
    // DEA number CRUD
    getV2ProviderEprescriptionsDeaNumbers: {
      providesTags: ['DeaNumbers'],
    },
    postV2ProviderEprescriptionsDeaNumbers: {
      invalidatesTags: ['DeaNumbers'],
    },
    putV2ProviderEprescriptionsDeaNumbersByDeaId: {
      invalidatesTags: ['DeaNumbers'],
    },
    deleteV2ProviderEprescriptionsDeaNumbersByDeaId: {
      invalidatesTags: ['DeaNumbers'],
    },
  },
});

// Export with user-friendly names (following feature pattern guidelines)
export const {
  useGetV2ProviderEprescriptionsStatusQuery: useGetEPrescriptionStatusQuery,
  useGetV2ProviderEprescriptionsRegistrationStatusQuery: useGetRegistrationStatusQuery,
  usePostV2ProviderEprescriptionsSetupClinicMutation: useSetupClinicMutation,
  usePostV2ProviderEprescriptionsSetupClinicianMutation: useSetupClinicianMutation,
  usePostV2ProviderPatientsByPatientIdEprescriptionsConnectMutation: useConnectPatientMutation,
  useGetV2ProviderPatientsByPatientIdEprescriptionsSsoQuery: useGetPatientSsoQuery,
  useLazyGetV2ProviderPatientsByPatientIdEprescriptionsSsoQuery: useLazyGetPatientSsoQuery,
  useLazyGetV2ProviderEprescriptionsSsoQuery: useLazyGetClinicianSsoQuery,
  // DEA number management
  useGetV2ProviderEprescriptionsDeaNumbersQuery: useGetDeaNumbersQuery,
  usePostV2ProviderEprescriptionsDeaNumbersMutation: useAddDeaNumberMutation,
  usePutV2ProviderEprescriptionsDeaNumbersByDeaIdMutation: useUpdateDeaNumberMutation,
  useDeleteV2ProviderEprescriptionsDeaNumbersByDeaIdMutation: useDeleteDeaNumberMutation,
} = eprescriptionsApi;
